/**
 * ShopThemeApplier — injiziert das im Startseiten-Editor konfigurierte
 * Erscheinungsbild (Farben + Fonts) als CSS-Custom-Properties in `:root`.
 *
 * Feb 2026 · Ein einzelner Mount-Point (im Root-Layout oder auf Shop-Seiten)
 * reicht — der Applier hört auf `hs:homepage-preview` (Live-Preview aus dem
 * Editor) und liest im Zweifel `/api/shop/homepage-config` einmalig aus dem
 * Cache. Für Sub-Shops wird das Config-Loading in ``SubShopPage.jsx``
 * gemacht — dort schreiben wir das Theme in denselben Cache-Slot, aus dem
 * dieser Applier liest.
 */
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { readShopCache, writeShopCache, CK, subShopHomepageKey } from '@/lib/shopCache';
import { useCustomFonts } from '@/admin/pagebuilder/customFonts';

const API = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_THEME = {
  enabled: false,
  button_color: '#C9A23C',
  button_color_hover: '#E0BE5A',
  button_text_color: '#3a2e08',
  text_color: '#1f1b16',
  heading_color: '#171512',
  link_color: '#8a6d10',
  surface_color: '#F7F5F1',
  body_font: 'Manrope',
  body_font_size: 16,
  heading_font: 'Fraunces',
};

function applyThemeVars(theme) {
  if (!theme || !theme.enabled) {
    // Reset auf CSS-Defaults
    const root = document.documentElement.style;
    [
      '--hs-gold', '--hs-gold-hover', '--hs-gold-dark',
      '--hs-text', '--hs-dark', '--hs-bg', '--hs-btn-text',
      '--shop-body-font', '--shop-heading-font',
      '--shop-body-size',
    ].forEach((v) => root.removeProperty(v));
    return;
  }
  const r = document.documentElement.style;
  r.setProperty('--hs-gold', theme.button_color);
  r.setProperty('--hs-gold-hover', theme.button_color_hover);
  r.setProperty('--hs-gold-dark', theme.link_color);
  r.setProperty('--hs-text', theme.text_color);
  r.setProperty('--hs-dark', theme.heading_color);
  r.setProperty('--hs-bg', theme.surface_color);
  r.setProperty('--shop-body-font', `'${theme.body_font}', system-ui, sans-serif`);
  r.setProperty('--shop-body-size', `${Number(theme.body_font_size) || 16}px`);
  r.setProperty('--shop-heading-font', `'${theme.heading_font}', Georgia, serif`);
  r.setProperty('--hs-btn-text', theme.button_text_color);
}

function applyDesignColorVars(items = []) {
  const root = document.documentElement.style;
  Array.from(root).filter((name) => name.startsWith('--hs-design-')).forEach((name) => root.removeProperty(name));
  items.forEach((item) => {
    const id = String(item.id || '').replace(/[^a-zA-Z0-9_-]/g, '');
    if (id && /^#[0-9a-f]{6}$/i.test(item.value || '')) root.setProperty(`--hs-design-${id}`, item.value);
  });
}

export default function ShopThemeApplier({ subShopSlug: subShopSlugProp = null }) {
  useCustomFonts();
  // Feb 2026 · Route-basierte Sub-Shop-Erkennung: greift ohne Prop-Drilling.
  const loc = useLocation();
  const routeSlug = (() => {
    const m = loc?.pathname?.match(/^\/themenwelten\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  })();
  const subShopSlug = subShopSlugProp || routeSlug;
  const [theme, setTheme] = useState(null);

  useEffect(() => {
    let cancelled = false;
    // 1. Aus Cache lesen (schneller Erst-Render, kein Flash)
    const cacheKey = subShopSlug ? subShopHomepageKey(subShopSlug) : CK.HOMEPAGE;
    const cached = readShopCache(cacheKey);
    if (cached?.theme) setTheme(cached.theme);
    // 2. Immer revalidieren. Der Cache verhindert den Flash, darf aber neue
    // Theme-Werte aus dem Startseiteneditor nicht dauerhaft verdecken.
    const url = subShopSlug
      ? `${API}/api/shop/homepage-config?sub_shop=${encodeURIComponent(subShopSlug)}`
      : `${API}/api/shop/homepage-config`;
    axios.get(url)
      .then((r) => {
        if (cancelled || !r.data) return;
        try { writeShopCache(cacheKey, r.data); } catch { /* ignore quota */ }
        setTheme(r.data.theme || DEFAULT_THEME);
      })
      .catch(() => { /* Cache/Defaults bleiben */ });
    axios.get(`${API}/api/shop/design-colors`)
      .then((r) => { if (!cancelled) applyDesignColorVars(r.data?.items || []); })
      .catch(() => {});
    // 3. Live-Preview-Listener (postMessage aus dem Editor-Iframe)
    const onMsg = (ev) => {
      const msg = ev.data;
      if (msg?.type === 'hs:homepage-preview' && msg.cfg?.theme) {
        setTheme(msg.cfg.theme);
      }
      if (msg?.type === 'hs:design-colors') applyDesignColorVars(msg.items || []);
    };
    const onDesignColors = (event) => applyDesignColorVars(event.detail || []);
    window.addEventListener('message', onMsg);
    window.addEventListener('hs:design-colors', onDesignColors);
    return () => {
      cancelled = true;
      window.removeEventListener('message', onMsg);
      window.removeEventListener('hs:design-colors', onDesignColors);
    };
  }, [subShopSlug]);

  useEffect(() => { applyThemeVars(theme); }, [theme]);

  return null;
}
